// Procedural sound effects. No audio files: everything is synthesized with
// Web Audio, which also keeps the game working offline.

const MUTE_KEY = 'hexagon.muted';

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (isMuted()) return null;
  try {
    ctx ??= new AudioContext();
    // Browsers start the context suspended until a user gesture; any click resumes it.
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    // Not persisted; fine.
  }
}

function tone(freq: number, start: number, duration: number, type: OscillatorType = 'sine', gain = 0.15): void {
  const a = audio();
  if (!a) return;
  const t = a.currentTime + start;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(gain, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
  osc.connect(g).connect(a.destination);
  osc.start(t);
  osc.stop(t + duration + 0.02);
}

function click(start: number, gain = 0.25): void {
  const a = audio();
  if (!a) return;
  const t = a.currentTime + start;
  const len = Math.floor(a.sampleRate * 0.03);
  const buf = a.createBuffer(1, len, a.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 3;
  const src = a.createBufferSource();
  const g = a.createGain();
  g.gain.value = gain;
  src.buffer = buf;
  src.connect(g).connect(a.destination);
  src.start(t);
}

export const sounds = {
  dice(): void {
    [0, 0.07, 0.15, 0.26, 0.34].forEach((t, i) => click(t, 0.3 - i * 0.04));
  },
  build(): void {
    tone(330, 0, 0.12, 'triangle');
    tone(495, 0.08, 0.16, 'triangle');
  },
  yourTurn(): void {
    tone(660, 0, 0.18);
    tone(880, 0.14, 0.28);
  },
  trade(): void {
    tone(520, 0, 0.1, 'square', 0.06);
    tone(780, 0.09, 0.12, 'square', 0.06);
  },
  win(): void {
    [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 0.12, 0.3, 'triangle'));
  },
};
